import { useEffect, useState } from "react";
import { listFieldDefinitions, type ClubRow, type EventClassConfig, type EventRow } from "../lib/api";

// The create/edit event form, shared by Admin -> Events and Manage -> <club>
// -> Events. Admin passes `clubs` (club picker) and `showFeatured` (front-page
// curation); Manage passes neither, since the club comes from the URL and
// featuring is a service-site decision.

/** Race classes should match vehicle classes exactly, so pull the options
 *  from the vehicle "class" field definition rather than free text. It's
 *  currently stored under appliesTo:"user" though, not "vehicle" -- see
 *  TODO #11, the vehicle-shaped fields haven't been re-scoped yet -- so
 *  check both buckets instead of hardcoding one that might come up empty. */
export function useClassOptions(): string[] {
  const [classOptions, setClassOptions] = useState<string[]>([]);
  useEffect(() => {
    Promise.all([listFieldDefinitions("vehicle"), listFieldDefinitions("user")])
      .then(([v, u]) => {
        const classDef = v.fields.find((f) => f.fieldKey === "class") ?? u.fields.find((f) => f.fieldKey === "class");
        setClassOptions(classDef?.options ?? []);
      })
      .catch(() => {});
  }, []);
  return classOptions;
}

/** Drops unnamed class rows and rounds the fee -- what both forms do before saving. */
export function cleanEventForm(form: Partial<EventRow>, editing: boolean): Partial<EventRow> {
  if (editing) {
    return {
      ...form,
      classes: form.classes ? form.classes.filter((c) => (c.name ?? "").trim().length > 0) : undefined,
      entryFeeCents: form.entryFeeCents !== undefined ? Math.round(Number(form.entryFeeCents)) : undefined,
    };
  }
  return {
    ...form,
    classes: (form.classes ?? []).filter((c) => (c.name ?? "").trim().length > 0),
    entryFeeCents: Math.round(Number(form.entryFeeCents) || 0),
  };
}

/** Registration open/close are compared server-side (lib/checkout.ts), so
 *  they're stored as full UTC ISO strings; the inputs show local time. */
function toLocalInput(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInput(v: string): string {
  // "" clears the field on update (an omitted key would leave it unchanged).
  return v ? new Date(v).toISOString() : "";
}

/** Per-class qualifying-rounds/mains config editor. Counts are config only --
 *  heats and results are entered by hand later, nothing here auto-generates
 *  them. classOptions (see useClassOptions) drives a dropdown instead of
 *  free text, so race classes match vehicle classes exactly -- falls back to
 *  a text input if no such field definition is set up yet. */
export function ClassConfigEditor({
  classes,
  onChange,
  classOptions,
}: {
  classes: EventClassConfig[];
  onChange: (classes: EventClassConfig[]) => void;
  classOptions: string[];
}) {
  function update(i: number, patch: Partial<EventClassConfig>) {
    onChange(classes.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  }
  function remove(i: number) {
    onChange(classes.filter((_, idx) => idx !== i));
  }
  return (
    <div>
      {classes.map((c, i) =>
        classOptions.length > 0 ? (
          <div className="row" key={i} style={{ marginBottom: "0.4rem" }}>
            <select value={c.name ?? ""} onChange={(e) => update(i, { name: e.target.value })}>
              <option value="" disabled>— Select a class —</option>
              {c.name && !classOptions.includes(c.name) && (
                <option value={c.name}>{c.name} (not in list)</option>
              )}
              {classOptions.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
            {classConfigFields(c, i, update, remove)}
          </div>
        ) : (
          <div className="row" key={i} style={{ marginBottom: "0.4rem" }}>
            <input
              placeholder="Class name"
              value={c.name ?? ""}
              onChange={(e) => update(i, { name: e.target.value })}
            />
            {classConfigFields(c, i, update, remove)}
          </div>
        ),
      )}
      <button
        type="button"
        className="secondary"
        onClick={() => onChange([...classes, { name: "", qualifyingRounds: 0, mains: 0 }])}
      >
        + Add class
      </button>
    </div>
  );
}

/** Qualifiers/Mains/Remove controls shared by both the select and text-input
 *  rows above -- only the class-name field itself differs between them. */
function classConfigFields(
  c: EventClassConfig,
  i: number,
  update: (i: number, patch: Partial<EventClassConfig>) => void,
  remove: (i: number) => void,
) {
  return (
    <>
      <input
        type="number"
        min={0}
        placeholder="Qualifiers"
        value={c.qualifyingRounds || ""}
        onChange={(e) => update(i, { qualifyingRounds: parseInt(e.target.value, 10) || 0 })}
        style={{ maxWidth: 110 }}
      />
      <input
        type="number"
        min={0}
        placeholder="Mains"
        value={c.mains || ""}
        onChange={(e) => update(i, { mains: parseInt(e.target.value, 10) || 0 })}
        style={{ maxWidth: 90 }}
      />
      <button type="button" className="danger" style={{ flex: "0 0 auto" }} onClick={() => remove(i)}>
        Remove
      </button>
    </>
  );
}

export interface EventFormProps {
  /** Prefix for input ids, unique per form on the page ("ce", "ee", ...). */
  idPrefix: string;
  heading: string;
  submitLabel: string;
  /** Editing an existing event: start date isn't required (a partial update
   *  keeps it), and unchecking Featured clears featuredOrder. */
  editing: boolean;
  value: Partial<EventRow>;
  onChange: (value: Partial<EventRow>) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel?: () => void;
  busy?: boolean;
  /** Track names for the Location field. */
  trackNames: string[];
  /** Location as free text with the tracks as suggestions (Manage), instead
   *  of a strict dropdown of the tracks (Admin). */
  freeTextLocation?: boolean;
  classOptions: string[];
  /** Admin only: show the club picker. */
  clubs?: ClubRow[];
  /** Admin only: show "Featured on front page". */
  showFeatured?: boolean;
  style?: React.CSSProperties;
}

export function EventForm({
  idPrefix: p,
  heading,
  submitLabel,
  editing,
  value: form,
  onChange: setForm,
  onSubmit,
  onCancel,
  busy,
  trackNames,
  freeTextLocation,
  classOptions,
  clubs,
  showFeatured,
  style,
}: EventFormProps) {
  return (
    <form onSubmit={onSubmit} className="card" style={style}>
      <h3>{heading}</h3>
      <div className="row">
        <div>
          <label htmlFor={`${p}-title`}>Title</label>
          <input id={`${p}-title`} value={form.title ?? ""} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
        </div>
        <div>
          <label htmlFor={`${p}-date`}>Start date</label>
          <input id={`${p}-date`} type="datetime-local" value={form.dateISO?.slice(0, 16) ?? ""} onChange={(e) => setForm({ ...form, dateISO: e.target.value })} required={!editing} />
        </div>
        <div>
          <label htmlFor={`${p}-end-date`}>End date (optional)</label>
          <input id={`${p}-end-date`} type="datetime-local" value={form.endDateISO?.slice(0, 16) ?? ""} onChange={(e) => setForm({ ...form, endDateISO: e.target.value })} />
        </div>
      </div>
      <div style={{ marginTop: "0.75rem" }}>
        <label htmlFor={`${p}-description`}>Description</label>
        <textarea id={`${p}-description`} rows={3} value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </div>
      <div className="row" style={{ marginTop: "0.75rem" }}>
        <div>
          <label htmlFor={`${p}-location`}>Location</label>
          {freeTextLocation ? (
            <>
              <input
                id={`${p}-location`}
                list={`${p}-location-tracks`}
                placeholder={trackNames.length ? "Pick a track or type a place" : "Where it's held"}
                value={form.location ?? ""}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
              />
              <datalist id={`${p}-location-tracks`}>
                {trackNames.map((n) => (
                  <option key={n} value={n} />
                ))}
              </datalist>
            </>
          ) : (
            <select id={`${p}-location`} value={form.location ?? ""} onChange={(e) => setForm({ ...form, location: e.target.value })}>
              <option value="">— Select a track —</option>
              {/* Keep whatever this event already had selected, even if it no longer matches a current track name. */}
              {form.location && !trackNames.includes(form.location) && (
                <option value={form.location}>{form.location} (not in track list)</option>
              )}
              {trackNames.map((n, i) => (
                <option key={`${n}-${i}`} value={n}>{n}</option>
              ))}
            </select>
          )}
        </div>
        {clubs && (
          <div>
            <label htmlFor={`${p}-club`}>Club</label>
            <select id={`${p}-club`} value={form.clubId ?? ""} onChange={(e) => setForm({ ...form, clubId: e.target.value || undefined })}>
              <option value="">— No club —</option>
              {clubs.map((c) => (
                <option key={c.clubId} value={c.clubId}>{c.name}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label htmlFor={`${p}-fee`}>Entry fee ($)</label>
          <input id={`${p}-fee`} type="number" step="0.01" value={form.entryFeeCents !== undefined ? Number(form.entryFeeCents) / 100 : ""} onChange={(e) => setForm({ ...form, entryFeeCents: Math.round(parseFloat(e.target.value || "0") * 100) })} />
        </div>
      </div>
      <div className="row" style={{ marginTop: "0.75rem" }}>
        <div>
          <label htmlFor={`${p}-reg-opens`}>Registration opens (optional)</label>
          <input id={`${p}-reg-opens`} type="datetime-local" value={toLocalInput(form.registrationOpensAt)} onChange={(e) => setForm({ ...form, registrationOpensAt: fromLocalInput(e.target.value) })} />
        </div>
        <div>
          <label htmlFor={`${p}-reg-closes`}>Registration closes (optional)</label>
          <input id={`${p}-reg-closes`} type="datetime-local" value={toLocalInput(form.registrationClosesAt)} onChange={(e) => setForm({ ...form, registrationClosesAt: fromLocalInput(e.target.value) })} />
        </div>
      </div>
      <div style={{ marginTop: "0.75rem" }}>
        <label>Classes</label>
        <ClassConfigEditor classes={form.classes ?? []} onChange={(classes) => setForm({ ...form, classes })} classOptions={classOptions} />
      </div>
      <div style={{ marginTop: "0.75rem" }}>
        <label htmlFor={`${p}-status`}>Status</label>
        <select id={`${p}-status`} value={form.status ?? "draft"} onChange={(e) => setForm({ ...form, status: e.target.value as EventRow["status"] })}>
          <option value="draft">draft</option>
          <option value="published">published</option>
        </select>
      </div>
      {showFeatured && (
        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginTop: "0.75rem" }}>
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={!!form.featured}
            onChange={(e) =>
              setForm(
                editing
                  ? { ...form, featured: e.target.checked, featuredOrder: e.target.checked ? form.featuredOrder : undefined }
                  : { ...form, featured: e.target.checked },
              )
            }
          />
          Featured on front page
        </label>
      )}
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
        <button type="submit" disabled={busy}>{submitLabel}</button>
        {onCancel && <button type="button" className="secondary" onClick={onCancel}>Cancel</button>}
      </div>
    </form>
  );
}
